import { useCallback, useRef, useState } from 'react';

import { useFormField } from '@/_components';
import { snackbar } from '@/_shared';

import type { UseFilesInputProps, UseFilesInputReturn } from './types';

import { useCreateFileMutation, useUpdateFileMutation } from '../api';
import { useMedia } from '../use-media';

export function useFilesInput<T>({
  value = [],
  allowedTypes,
  shape,
  size,
  folder_path,
  access_level,
  imagesOnly,
  maxFiles,
  onChange,
  onPartialUpdate = () => Promise.resolve(),
}: UseFilesInputProps<T>): UseFilesInputReturn {
  const isSingleFile = maxFiles === 1;

  const { onImageUpdate: onMediaUpdate, isPending: isMediaUpdatePending } = useMedia<T>(onPartialUpdate, false);
  const { name: fieldName } = useFormField();

  const { mutateAsync: uploadFile } = useCreateFileMutation();
  const { mutateAsync: updateFile } = useUpdateFileMutation();

  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  let dimensions = size === 'lg' ? 'size-24 md:size-28' : 'size-24';
  if (shape === 'rectangle') {
    dimensions = size === 'lg' ? 'h-24 w-[7.84rem] md:h-28 md:w-[9.49rem]' : 'h-24 w-[7.84rem]';
  }
  if (size === 'fluid') {
    dimensions = 'w-full h-36';
  }

  dimensions = `${dimensions} max-w-full`;

  // Filter files to ensure they match allowed types and image-only constraints
  const filterValidFiles = useCallback(
    (files: File[]) => {
      return files.filter((file) => {
        if (imagesOnly && !file.type.startsWith('image/')) {
          snackbar.error(`Only image files are allowed. "${file.name}" is not an image.`);
          return false;
        }
        return allowedTypes?.includes('*/*') || allowedTypes?.includes(file.type);
      });
    },
    [allowedTypes, imagesOnly]
  );

  // Upload files in modal mode (for multiple files)
  const processMultipleFiles = useCallback(
    async (files: File[]) => {
      const uploadFilePromises = files.map((file) =>
        uploadFile({
          name: file.name,
          access_level,
          folder_path,
          media: file,
        })
      );
      const uploadedFiles = await Promise.all(uploadFilePromises);
      return uploadedFiles.map((file) => file.data);
    },
    [uploadFile, access_level, folder_path]
  );

  // Handles file selection for both single and multiple file modes
  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = event.target.files;
      if (!fileList || fileList.length === 0) return;

      const files = Array.from(fileList);
      const validFiles = filterValidFiles(files);

      if (validFiles.length === 0) {
        if (files.length > 0) {
          snackbar.error(`Selected ${files.length > 1 ? 'files are' : 'file is'} not allowed.`);
        }
        return;
      }

      const isSingleFileMode = maxFiles === 1;

      // If in single file mode, upload directly without showing modal
      if (isSingleFileMode) {
        setIsPending(true);
        try {
          const uploadedFiles = await processMultipleFiles(validFiles);

          if (isSingleFile) {
            await onMediaUpdate('CREATE', fieldName, uploadedFiles);
            onChange?.(uploadedFiles);
          } else {
            onChange?.(uploadedFiles);
          }
        } catch (error) {
          console.error('File upload failed:', error);
          snackbar.error('Failed to upload file. Please try again.');
        } finally {
          setIsPending(false);
        }
      } else {
        // For multiple files, show the modal
        setSelectedFiles((prevFiles) => {
          // If modal is already open, append new files to existing ones
          if (isOpen) {
            // Filter out duplicate files by name
            const existingFileNames = new Set(prevFiles.map((file) => file.name));
            const uniqueNewFiles = validFiles.filter((file) => !existingFileNames.has(file.name));
            return [...prevFiles, ...uniqueNewFiles];
          }
          // Otherwise just set the new files
          return validFiles;
        });
        setIsOpen(true);
      }
    },
    [filterValidFiles, maxFiles, processMultipleFiles, isSingleFile, onMediaUpdate, fieldName, onChange, isOpen]
  );

  const handleUpload = useCallback(async () => {
    setIsPending(true);
    try {
      const uploadedFiles = await processMultipleFiles(selectedFiles);
      const uniqueFiles = new Set([...value, ...uploadedFiles]);
      const newFiles = Array.from(uniqueFiles);

      if (isSingleFile) {
        await onMediaUpdate('CREATE', fieldName, newFiles);
        onChange?.(newFiles);
      } else {
        onChange?.(newFiles);
      }

      setIsOpen(false);
      setSelectedFiles([]);
    } catch (error) {
      console.error('File upload failed:', error);
    } finally {
      setIsPending(false);
    }
  }, [processMultipleFiles, selectedFiles, value, isSingleFile, onMediaUpdate, fieldName, onChange]);

  const handleUpdate = useCallback(
    async (fileId: number, file: File) => {
      setIsPending(true);
      try {
        const response = await updateFile({
          id: fileId.toString(),
          name: file.name,
          access_level,
          folder_path,
          media: file,
        });

        const updatedFiles = value.map((f) => (f.id === fileId ? response.data : f));

        await onMediaUpdate('UPDATE', fieldName, updatedFiles);
        onChange?.(updatedFiles);
      } catch (error) {
        console.error('File update failed:', error);
      } finally {
        setIsPending(false);
      }
    },
    [access_level, folder_path, updateFile, value, onMediaUpdate, fieldName, onChange]
  );

  const handleCancel = useCallback(() => {
    setIsOpen(false);
    setSelectedFiles([]);
  }, []);

  const handleRemove = useCallback(
    async (fileId: number) => {
      const newFiles = value?.filter((file) => file.id !== fileId) ?? [];

      if (onMediaUpdate) {
        await onMediaUpdate('DELETE', fieldName, newFiles);
        onChange?.(newFiles);
      } else {
        onChange?.(newFiles);
      }
    },
    [value, onMediaUpdate, fieldName, onChange]
  );

  const handleRemoveSelected = useCallback((fileName: string) => {
    setSelectedFiles((prev) => prev.filter((file) => file.name !== fileName));
  }, []);

  return {
    files: value ?? [],
    dimensions,
    selectedFiles,
    isPending,
    modalOpen: [isOpen, setIsOpen],
    inputRef: fileInputRef as React.RefObject<HTMLInputElement>,
    isSingleFile,
    isMediaUpdatePending,
    handleFileSelect,
    handleUpload,
    handleCancel,
    handleRemove,
    handleRemoveSelected,
    handleUpdate,
  };
}
